import {
  EmployeeApprovalDocument,
  PrivateDocumentListResponse,
  PrivateDocumentUploadListResponse
} from '../models/employeeDocument';

/**
 * Adds the documents returned from a multi-employee save to the employees they were saved for, so
 * the approval grid shows them without reloading the whole list. The save response carries no
 * creator, so every document is attributed to the given user.
 * @param employees The employee rows of the grid, unfiltered
 * @param uploadedDocuments The saved documents, one entry per document and employee
 * @param createdBy The name shown as the creator of the new documents
 * @returns The documents that were added to an employee
 */
export function addUploadedDocuments(
  employees: EmployeeApprovalDocument[],
  uploadedDocuments: PrivateDocumentUploadListResponse[],
  createdBy: string
): PrivateDocumentListResponse[] {
  const added: PrivateDocumentListResponse[] = [];
  uploadedDocuments.forEach(({ EmployeeId, ...uploaded }) => {
    const employee = employees.find(e => e.EmployeeId === EmployeeId);
    if (!employee) {
      return;
    }

    const document: PrivateDocumentListResponse = {
      ...uploaded,
      ApprovedOn: null,
      CreatedBy: createdBy
    };
    // A new array, so the nested document table picks up the change.
    employee.EmployeeDocuments = [
      ...(employee.EmployeeDocuments ?? []),
      document
    ];
    updateDocumentCounts(employee);
    added.push(document);
  });
  return added;
}

/**
 * Removes a deleted document from the employee it belongs to and recalculates that employee's
 * counts, so the approval grid reflects the deletion without reloading the whole list.
 * @param employees The employee rows of the grid, unfiltered
 * @param documentId The deleted document
 */
export function removeDocument(
  employees: EmployeeApprovalDocument[],
  documentId: number
): void {
  const employee = employees.find(e =>
    (e.EmployeeDocuments ?? []).some(d => d.Id === documentId)
  );
  if (!employee) {
    return;
  }

  // A new array, so the nested document table picks up the change.
  employee.EmployeeDocuments = employee.EmployeeDocuments.filter(
    d => d.Id !== documentId
  );
  updateDocumentCounts(employee);
}

/**
 * Recalculates an employee's document counts from its documents, the same way the backend does.
 * @param employee The employee row whose documents changed
 */
export function updateDocumentCounts(employee: EmployeeApprovalDocument): void {
  const documents = employee.EmployeeDocuments ?? [];
  employee.TotalDocuments = documents.length;
  employee.TotalApprovedDocuments = documents.filter(
    d => d.ApprovedOn != null
  ).length;
  updateApprovedStatus(employee);
}

/**
 * Sets the approval status shown in the grid, as approved documents out of all documents
 * (e.g. "2/5").
 * @param employee The employee row whose counts are set
 */
export function updateApprovedStatus(employee: EmployeeApprovalDocument): void {
  employee.ApprovedStatus =
    employee.TotalApprovedDocuments + '/' + employee.TotalDocuments;
}
